// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * Pure data helpers for the fitness charts (slice 09, PRD §M9 / US §5 §6).
 *
 * Why client-side computation:
 *   - The `CycleSnapshotDto.all_scores_by_iter` payload carries the
 *     full per-iter distribution as raw arrays (PRD §Interfaces M3,
 *     legacy iso panel input). Recharts has no native box plot ;
 *     ApexCharts has a `boxPlot` series but the existing chart-lib
 *     precedent in this codebase is empty — we deliberately stay on
 *     Recharts (no new deps per the issue's "no new chart library"
 *     rule) and render a candlestick-style 5-number summary.
 *   - Best-per-iter for the trajectory chart is derived as
 *     `max(all_scores_by_iter[i])` to avoid coupling to the snapshot's
 *     `best_agent_scores` field (which is the *current* top-K at the
 *     time of the snapshot, NOT a per-iter time series — see PRD §M3).
 *     This makes the trajectory chart strictly iso with the legacy
 *     panel and resilient to future K-cap changes.
 *
 * Both functions are total, pure, and stable under repeated calls.
 */

/**
 * Five-number summary used by the fitness-distribution candlestick :
 *   - `min` / `max` — the extrema of the distribution.
 *   - `q1` / `median` / `q3` — quartiles via the standard linear-
 *     interpolation method (Tukey's R-7 / NumPy default). For lists of
 *     length ≤ 2 the median is the mean of the two values, and the
 *     quartiles collapse to min/max so the candle still renders a
 *     coherent shape.
 *   - `iter_index` — the originating iter, propagated so the chart can
 *     align the X-axis with the trajectory chart's X-axis (PRD §M9
 *     "X-axis shared across both charts").
 *
 * The empty-distribution case (`scores = []`) returns `null` ; the
 * caller filters it out before passing to the chart series so the
 * chart never renders a degenerate point.
 */
export interface DistributionSummary {
  iter_index: number
  min: number
  q1: number
  median: number
  q3: number
  max: number
}

/**
 * Tukey R-7 (linear interpolation between the two nearest ranks) —
 * matches NumPy's default `np.percentile` behaviour. `p` is in `[0, 1]`.
 *
 * `sorted` must already be sorted ascending ; the caller is expected
 * to sort once and pass it three times (for q1/median/q3).
 */
const percentileR7 = (sorted: readonly number[], p: number): number => {
  if (sorted.length === 0) return Number.NaN
  if (sorted.length === 1) return sorted[0]
  const rank = p * (sorted.length - 1)
  const lo = Math.floor(rank)
  const hi = Math.ceil(rank)
  if (lo === hi) return sorted[lo]
  const frac = rank - lo
  return sorted[lo] * (1 - frac) + sorted[hi] * frac
}

/**
 * Build the 5-number summary for a single iter's distribution.
 *
 * Returns `null` for empty input — the caller skips empty iters.
 */
export const summarizeDistribution = (
  scores: readonly number[],
  iter_index: number,
): DistributionSummary | null => {
  if (scores.length === 0) return null
  const sorted = [...scores].sort((a, b) => a - b)
  return {
    iter_index,
    min: sorted[0],
    q1: percentileR7(sorted, 0.25),
    median: percentileR7(sorted, 0.5),
    q3: percentileR7(sorted, 0.75),
    max: sorted[sorted.length - 1],
  }
}

/**
 * Build the per-iter summary list for the distribution chart.
 *
 * Empty inner iters are filtered out so the chart's X-axis only
 * carries iters that have at least one score.
 */
export const summarizeAllIters = (
  allScoresByIter: readonly (readonly number[])[],
): DistributionSummary[] => {
  const out: DistributionSummary[] = []
  for (let i = 0; i < allScoresByIter.length; i += 1) {
    const summary = summarizeDistribution(allScoresByIter[i], i)
    if (summary !== null) out.push(summary)
  }
  return out
}
